/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { KTIcon } from '../../../../helpers'
import { Column, useSortBy, useTable } from 'react-table'
import moment from 'moment'
import { axiosClientAuth } from '../../../../../app/utils'
import { useNavigate } from 'react-router-dom'

type Props = {
  className: string
}

type columnType = {
  name: string
  pe_id: string
  created_at: string
  is_activated: boolean
  uuid: any
}

const TablesWidgetOperatorList: React.FC<Props> = ({ className }) => {
  const intl = useIntl()
  const navigate = useNavigate()

  const [peData, setPeData] = useState<columnType[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState('')

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, activeFilter])

  const fetchData = (currentPage: number, currentPageSize: number, searchVal: string, active: string) => {
    setLoading(true)
    axiosClientAuth
      .get('/auth/users/', {
        params: {
          type: 'PE',
          page: currentPage,
          page_size: currentPageSize,
          ...(searchVal ? { search: searchVal } : {}),
          ...(active !== '' ? { active } : {}),
        },
        transformResponse: [
          (data: string) => {
            const safe = data.replace(/"id"\s*:\s*(\d{15,})/g, '"id":"$1"')
            return JSON.parse(safe)
          },
        ],
      })
      .then((res) => {
        const { count, total_pages, results } = res.data.data
        setTotalCount(count)
        setTotalPages(total_pages)
        setPeData(
          results.map((item: any) => ({
            name: item.name,
            pe_id: String(item.id),
            created_at: item.created_at,
            is_activated: item.status?.self?.active ?? false,
            uuid: item.id,
          }))
        )
      })
      .catch((err) => console.log(err))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    fetchData(page, pageSize, debouncedSearch, activeFilter)
  }, [page, pageSize, debouncedSearch, activeFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  const columns: ReadonlyArray<Column<columnType>> = useMemo(
    () => [
      {
        Header: intl.formatMessage({ id: 'TABLE.SL_NO', defaultMessage: 'S.No' }),
        id: 'serial-number',
        Cell: ({ row }: any) => (page - 1) * pageSize + row.index + 1,
      } as unknown as Column<columnType>,
      {
        Header: intl.formatMessage({ id: 'TABLE.PE_DETAILS_COL', defaultMessage: 'PE DETAILS' }),
        accessor: 'name',
        Cell: ({ ...props }: any) => (
          <>
            <span>{props.value}</span>
            <span className='d-block text-muted fw-semibold'>{props.row.original.pe_id}</span>
          </>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.CREATED_AT_COL', defaultMessage: 'CREATED AT' }),
        accessor: 'created_at',
        Cell: ({ value }: any) => <span>{moment(value).format('DD MMM YYYY')}</span>,
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.STATUS', defaultMessage: 'STATUS' }),
        accessor: 'is_activated',
        Cell: ({ value }: any) => (
          <span className={`badge badge-light-${value ? 'success' : 'danger'} fw-bold px-4 py-3`}>
            {value ? intl.formatMessage({ id: 'COMMON.ACTIVE', defaultMessage: 'Active' }) : intl.formatMessage({ id: 'COMMON.NOT_ACTIVE', defaultMessage: 'Not active' })}
          </span>
        ),
      },
      {
        Header: intl.formatMessage({ id: 'TABLE.ACTION', defaultMessage: 'ACTION' }),
        id: 'col-action',
        Cell: ({ row }: any) => (
          <button
            type='button'
            className='btn h-35px w-65px btn-icon btn-light me-3'
            onClick={() => navigate(`/operatorsummary?user=${row.original.uuid}`, { state: { name: row.original.name, pe_id: row.original.pe_id } })}
          >
            {intl.formatMessage({ id: 'COMMON.VIEW', defaultMessage: 'View' })}
          </button>
        ),
      },
    ],
    [page, pageSize, intl]
  )

  const operatorListTable = useTable(
    { columns, data: peData },
    useSortBy
  )

  return (
    <div className={`card ${className}`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>{intl.formatMessage({ id: 'OPERATOR.ACCOUNT_LIST', defaultMessage: 'Account List' })}</span>
          <span className='text-muted fw-semibold fs-7'>{intl.formatMessage({ id: 'OPERATOR.TOTAL_ACCOUNT', defaultMessage: 'Total {count} account' }, { count: totalCount })}</span>
        </h3>
        <div className='d-flex align-items-center'>
          <select
            className='form-select form-select-sm form-select-solid w-auto'
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
          >
            <option value=''>{intl.formatMessage({ id: 'COMMON.ALL', defaultMessage: 'All' })}</option>
            <option value='true'>{intl.formatMessage({ id: 'COMMON.ACTIVE', defaultMessage: 'Active' })}</option>
            <option value='false'>{intl.formatMessage({ id: 'COMMON.NOT_ACTIVE', defaultMessage: 'Not active' })}</option>
          </select>
          <div className='d-flex align-items-center position-relative my-1 ms-2'>
            <KTIcon iconName='magnifier' className='text-white-500 position-absolute ms-2' />
            <input
              type='text'
              className='form-control form-control-solid form-select-sm w-150px ps-9'
              placeholder={intl.formatMessage({ id: 'OPERATOR.SEARCH_ACCOUNT', defaultMessage: 'Search Account' })}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className='card-body pt-3'>
        <div className='table-responsive'>
          <table
            {...operatorListTable.getTableProps()}
            className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
          >
            <thead>
              {operatorListTable.headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      className={column.id === 'col-action' ? 'w-120px' : ''}
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                    >
                      <span className='text-gray-400 fs-7'>{column.render('Header')}</span>
                      {column.isSorted ? (
                        column.isSortedDesc ? (
                          <KTIcon iconName='down' className='text-gray ms-1' />
                        ) : (
                          <KTIcon iconName='up' className='text-gray ms-1' />
                        )
                      ) : null}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...operatorListTable.getTableBodyProps()}>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className='text-center'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : operatorListTable.rows.length > 0 ? (
                operatorListTable.rows.map((row) => {
                  operatorListTable.prepareRow(row)
                  return (
                    <tr {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <td className='fw-bold' {...cell.getCellProps()}>
                          {cell.render('Cell')}
                        </td>
                      ))}
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={columns.length} className='text-center'>
                    <span className='fw-bold'>No data found</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className='d-flex justify-content-between align-items-center mt-5'>
            <div>
              <select
                className='form-select form-select-sm form-select-solid'
                value={pageSize}
                onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1) }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div>
              <span>
                Page <strong>{page} of {totalPages}</strong>
              </span>
            </div>
            <div>
              <button
                className='btn btn-sm'
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
              >
                <KTIcon iconName='left' className='text-gray fs-1 me-5' />
              </button>
              <button
                className='btn btn-sm'
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
              >
                <KTIcon iconName='right' className='text-gray fs-1 me-5' />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export { TablesWidgetOperatorList }
